import { cn } from 'cn';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { announce } from '@/app/announcer';

/**
 * The header pattern every screen uses: eyebrow, title (Fraunces), a short
 * description, actions, a meta row and a tabs slot. Route focus lands on the
 * title and document.title follows it. `compact` turns on the sticky strip
 * the matter hub needs: once the header scrolls away a 44 px strip with the
 * essentials stays under the top bar.
 */
export type PageHeaderProps = {
  title: string;
  /** What the tab and the announcer say when it differs from the visible title (the hub's reference). */
  documentTitle?: string;
  eyebrow?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
  tabs?: ReactNode;
  compact?: ReactNode;
  className?: string;
};

export function PageHeader({ title, documentTitle, eyebrow, description, actions, meta, tabs, compact, className }: PageHeaderProps) {
  const h1 = useRef<HTMLHeadingElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  const announced = documentTitle ?? title;

  useEffect(() => {
    announce(announced);
    h1.current?.focus({ preventScroll: true });
  }, [announced]);

  useEffect(() => {
    if (!compact || !sentinel.current) return;
    const io = new IntersectionObserver(([entry]) => setStuck(entry ? !entry.isIntersecting : false), { rootMargin: '-52px 0px 0px 0px' });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [compact]);

  return (
    <>
      <div ref={sentinel} aria-hidden="true" />
      <div data-slot="page-header" className={cn('flex flex-col gap-3 pt-3', className)}>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6">
          <div className="min-w-0">
            {eyebrow ? <p className="eyebrow mb-1 text-beam-text-muted">{eyebrow}</p> : null}
            <h1 ref={h1} id="page-title" tabIndex={-1} className="font-heading text-[22px] leading-[1.25] font-medium outline-none md:text-[26px]">
              {title}
            </h1>
            {description ? <p className="mt-1 max-w-[60ch] text-sm text-beam-text-muted">{description}</p> : null}
            {meta ? <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px]">{meta}</div> : null}
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
        {tabs}
      </div>
      {compact ? (
        <div
          data-slot="page-header-compact"
          data-stuck={stuck}
          className={cn(
            'fixed inset-x-0 top-14 z-30 border-b border-beam-line bg-beam-paper/95 backdrop-blur transition-opacity duration-[140ms] md:top-[52px]',
            stuck ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
          aria-hidden={!stuck}
        >
          {/* Mounted only while stuck, so its controls never duplicate the header's in the DOM. */}
          <div className="mx-auto flex h-11 max-w-[1448px] items-center justify-between gap-3 px-4 md:px-6">{stuck ? compact : null}</div>
        </div>
      ) : null}
    </>
  );
}
